/**
 * レイアウトの枠と「テキスト」アコーディオンの6欄の対応 (docs/features/banner-layout-template-plan.md §13)。
 *
 * レイアウトは配置だけを持ち、文言は持たない (2026-10-18 方針変更)。文言の正本は6欄で、各 kind の**最初の枠**にその欄の値が入る。
 * 2個目以降の同種の枠や、欄が空の枠の文言は AI が考える (「案件情報からのテキスト生成」が「生成しない」なら枠を外す)。
 * 規則は backend/services/banner_layout/prompt.py (assign_texts) と同じ。
 */
import type { BannerLayoutKind } from './roles';
import type { BannerLayoutElement } from './types';

/** kind ⇄ 属性キー。text は「テキスト（商品名・キャンペーン名・特典など）」(display_text)、price は「価格」(price_text)、date は「日付」(date_text) */
export const LAYOUT_TEXT_ATTRIBUTE_KEYS: Partial<Record<BannerLayoutKind, string>> = {
  main_copy: 'main_copy',
  cta: 'cta',
  disclaimer: 'disclaimer_text',
  text: 'display_text',
  price: 'price_text',
  date: 'date_text',
};

/** kind → 「テキスト」アコーディオンの欄名 */
export const LAYOUT_TEXT_FIELD_LABELS: Partial<Record<BannerLayoutKind, string>> = {
  main_copy: 'メインコピー',
  cta: 'CTA',
  disclaimer: '注意書き',
  text: 'テキスト（商品名・キャンペーン名・特典など）',
  price: '価格',
  date: '日付',
};

function attrString(attributes: Record<string, unknown> | undefined, key: string): string {
  const v = attributes?.[key];
  return typeof v === 'string' ? v.trim() : '';
}

/** 要素 id → その枠に入る文言 (各 kind の最初の枠だけ・欄が空なら入れない)。キャンバスの表示とインスペクタに使う */
export function assignedLayoutTexts(
  elements: BannerLayoutElement[],
  attributes: Record<string, unknown> | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  const seen = new Set<string>();
  for (const el of elements) {
    const key = LAYOUT_TEXT_ATTRIBUTE_KEYS[el.kind];
    if (!key || seen.has(el.kind)) continue;
    seen.add(el.kind);
    const value = attrString(attributes, key);
    if (value) out[el.id] = value;
  }
  return out;
}

/** 要素が、その kind の最初の枠か (= 欄の値が入る枠か) */
export function isFirstOfKind(elements: BannerLayoutElement[], element: BannerLayoutElement): boolean {
  return elements.find((e) => e.kind === element.kind)?.id === element.id;
}

/** 保存・適用する要素から文言を外す (段階1のテンプレート・生成記録に残る text を持ち越さない) */
export function stripLayoutTexts(elements: BannerLayoutElement[]): BannerLayoutElement[] {
  return elements.map(({ text: _text, ...rest }) => rest);
}
